var _EditorImpl_markup, _EditorImpl_editorType, _EditorImpl_toolbarVisible, _EditorImpl_splitModeEnabled, _EditorImpl_splitMode, _EditorImpl_renderPreview, _EditorImpl_wysiwygEditor, _EditorImpl_markupEditor, _EditorImpl_allowHTML, _EditorImpl_linkify, _EditorImpl_linkifyTlds, _EditorImpl_attrs, _EditorImpl_extensions, _EditorImpl_renderStorage, _EditorImpl_fileUploadHandler, _EditorImpl_needToSetDimensionsForUploadedImages, _EditorImpl_prepareRawMarkup, _EditorImpl_cmFilesUploader;
import { __classPrivateFieldGet, __classPrivateFieldSet } from "tslib";
import CodeMirror from 'codemirror';
import { TextSelection } from 'prosemirror-state';
import { YfmEditor as WysiwygEditor, } from '../core';
import { i18n } from '../i18n/bundle';
import { logger } from '../logger';
import { Editor as MarkupEditor } from '../markup/editor';
import { Action as A, formatter as f } from '../shortcuts';
import { DataTransferType, isFilesFromHtml, isFilesOnly } from '../utils/clipboard';
import { SafeEventEmitter } from '../utils/event-emitter';
import { config as cmBaseConfig } from './cm-config';
import { CMFilesUploadManager } from './cm-upload';
const PAIRING_CHARS = new Map([
    ['(', ')'],
    ['{', '}'],
    ['[', ']'],
    ['<', '>'],
    ['*', '*'],
    ['~', '~'],
    ['"', '"'],
    ["'", "'"],
    ['`', '`'],
]);
/** @internal */
export class EditorImpl extends SafeEventEmitter {
    constructor(opts) {
        var _a, _b, _c, _d;
        super({ onError: logger.error.bind(logger) });
        _EditorImpl_markup.set(this, void 0);
        _EditorImpl_editorType.set(this, void 0);
        _EditorImpl_toolbarVisible.set(this, void 0);
        _EditorImpl_splitModeEnabled.set(this, void 0);
        _EditorImpl_splitMode.set(this, void 0);
        _EditorImpl_renderPreview.set(this, void 0);
        _EditorImpl_wysiwygEditor.set(this, void 0);
        _EditorImpl_markupEditor.set(this, void 0);
        _EditorImpl_allowHTML.set(this, void 0);
        _EditorImpl_linkify.set(this, void 0);
        _EditorImpl_linkifyTlds.set(this, void 0);
        _EditorImpl_attrs.set(this, void 0);
        _EditorImpl_extensions.set(this, void 0);
        _EditorImpl_renderStorage.set(this, void 0);
        _EditorImpl_fileUploadHandler.set(this, void 0);
        _EditorImpl_needToSetDimensionsForUploadedImages.set(this, void 0);
        _EditorImpl_prepareRawMarkup.set(this, void 0);
        _EditorImpl_cmFilesUploader.set(this, void 0);
        this.getValue = () => this.currentEditor.getValue();
        this.onCMCursorActivity = () => {
            this.emit('rerender-toolbar', null);
        };
        this.onCMChanges = () => {
            this.emit('change', null);
        };
        this.onCMPaste = (cm, event) => {
            var _a;
            if (!event.clipboardData)
                return;
            const { clipboardData } = event;
            const fromWysiwyg = clipboardData.types.includes(DataTransferType.Yfm);
            if (fromWysiwyg) {
                const markup = clipboardData.getData(DataTransferType.Yfm);
                cm.replaceSelection(markup);
                event.preventDefault();
                return;
            }
            if (clipboardData.files.length) {
                (_a = __classPrivateFieldGet(this, _EditorImpl_cmFilesUploader, "f")) === null || _a === void 0 ? void 0 : _a.upload(clipboardData.files);
            }
            if (isFilesOnly(clipboardData) || isFilesFromHtml(clipboardData)) {
                event.preventDefault();
            }
        };
        this.onCMBeforeChange = (_cm, event) => {
            const { text, to, from, cancel } = event;
            const selection = this.cm.getSelection();
            const sameLine = from.line === to.line;
            const newLine = selection.endsWith('\n');
            if (!PAIRING_CHARS.has(text[0]) ||
                event.origin !== '+input' ||
                selection.trim().length <= 0)
                return;
            cancel();
            const replacement = text[0] +
                // If there was a line break at the end, remove it and place it behind the closing paired character
                (newLine ? selection.replace(/\n+$/, '') : selection) +
                PAIRING_CHARS.get(text[0]) +
                (newLine ? '\n' : '');
            this.cm.replaceSelection(replacement);
            // If paired characters were inserted on the same line, then 2 characters will be added
            // if not, then one closing.
            let ch = sameLine ? to.ch + 2 : to.ch + 1;
            if (to.ch === 0)
                ch = 0;
            this.cm.addSelection(from, {
                line: to.line,
                ch,
            });
        };
        __classPrivateFieldSet(this, _EditorImpl_editorType, (_a = opts.initialEditorType) !== null && _a !== void 0 ? _a : 'wysiwyg', "f");
        __classPrivateFieldSet(this, _EditorImpl_toolbarVisible, Boolean(opts.initialToolbarVisible), "f");
        __classPrivateFieldSet(this, _EditorImpl_splitMode, (_b = (opts.renderPreview && opts.splitMode)) !== null && _b !== void 0 ? _b : false, "f");
        __classPrivateFieldSet(this, _EditorImpl_splitModeEnabled, (_c = (__classPrivateFieldGet(this, _EditorImpl_splitMode, "f") && opts.initialSplitModeEnabled)) !== null && _c !== void 0 ? _c : false, "f");
        __classPrivateFieldSet(this, _EditorImpl_renderPreview, opts.renderPreview, "f");
        __classPrivateFieldSet(this, _EditorImpl_markup, (_d = opts.initialMarkup) !== null && _d !== void 0 ? _d : '', "f");
        __classPrivateFieldSet(this, _EditorImpl_attrs, opts.attrs, "f");
        __classPrivateFieldSet(this, _EditorImpl_linkify, opts.linkify, "f");
        __classPrivateFieldSet(this, _EditorImpl_linkifyTlds, opts.linkifyTlds, "f");
        __classPrivateFieldSet(this, _EditorImpl_allowHTML, opts.allowHTML, "f");
        __classPrivateFieldSet(this, _EditorImpl_extensions, opts.extensions, "f");
        __classPrivateFieldSet(this, _EditorImpl_renderStorage, opts.renderStorage, "f");
        __classPrivateFieldSet(this, _EditorImpl_fileUploadHandler, opts.fileUploadHandler, "f");
        __classPrivateFieldSet(this, _EditorImpl_needToSetDimensionsForUploadedImages, Boolean(opts.needToSetDimensionsForUploadedImages), "f");
        __classPrivateFieldSet(this, _EditorImpl_prepareRawMarkup, opts.prepareRawMarkup, "f");
    }
    get _wysiwygView() {
        var _a;
        // @ts-expect-error internal typing
        return (_a = __classPrivateFieldGet(this, _EditorImpl_wysiwygEditor, "f")) === null || _a === void 0 ? void 0 : _a.view;
    }
    get currentType() {
        return __classPrivateFieldGet(this, _EditorImpl_editorType, "f");
    }
    set currentType(newType) {
        var _a, _b, _c;
        switch (newType) {
            case 'markup': {
                __classPrivateFieldSet(this, _EditorImpl_editorType, newType, "f");
                if (__classPrivateFieldGet(this, _EditorImpl_wysiwygEditor, "f")) {
                    const markupEditorValue = (_a = __classPrivateFieldGet(this, _EditorImpl_markupEditor, "f")) === null || _a === void 0 ? void 0 : _a.getValue();
                    const wysiwygEditorValue = __classPrivateFieldGet(this, _EditorImpl_wysiwygEditor, "f").getValue();
                    if (!markupEditorValue ||
                        this.shouldReplaceMarkupEditorValue(markupEditorValue, wysiwygEditorValue)) {
                        __classPrivateFieldSet(this, _EditorImpl_markup, wysiwygEditorValue, "f");
                    }
                    else {
                        __classPrivateFieldSet(this, _EditorImpl_markup, markupEditorValue, "f");
                    }
                }
                if (__classPrivateFieldGet(this, _EditorImpl_markupEditor, "f"))
                    __classPrivateFieldGet(this, _EditorImpl_markupEditor, "f").replace(__classPrivateFieldGet(this, _EditorImpl_markup, "f"));
                break;
            }
            case 'wysiwyg': {
                __classPrivateFieldSet(this, _EditorImpl_editorType, newType, "f");
                if (__classPrivateFieldGet(this, _EditorImpl_markupEditor, "f")) {
                    const value = __classPrivateFieldGet(this, _EditorImpl_markupEditor, "f").getValue();
                    __classPrivateFieldSet(this, _EditorImpl_markup, (_c = (_b = __classPrivateFieldGet(this, _EditorImpl_prepareRawMarkup, "f")) === null || _b === void 0 ? void 0 : _b.call(this, value)) !== null && _c !== void 0 ? _c : value, "f");
                }
                else if (__classPrivateFieldGet(this, _EditorImpl_prepareRawMarkup, "f")) {
                    __classPrivateFieldSet(this, _EditorImpl_markup, __classPrivateFieldGet(this, _EditorImpl_prepareRawMarkup, "f").call(this, __classPrivateFieldGet(this, _EditorImpl_markup, "f")), "f");
                }
                if (__classPrivateFieldGet(this, _EditorImpl_wysiwygEditor, "f"))
                    __classPrivateFieldGet(this, _EditorImpl_wysiwygEditor, "f").replace(__classPrivateFieldGet(this, _EditorImpl_markup, "f"));
                break;
            }
            default:
                throw new Error('Unknown editor type: ' + newType);
        }
        setTimeout(() => {
            this.currentEditor.focus();
        }, 30);
    }
    get toolbarVisible() {
        return __classPrivateFieldGet(this, _EditorImpl_toolbarVisible, "f");
    }
    get splitModeEnabled() {
        return __classPrivateFieldGet(this, _EditorImpl_splitModeEnabled, "f");
    }
    get splitMode() {
        return __classPrivateFieldGet(this, _EditorImpl_splitMode, "f");
    }
    get renderPreview() {
        return __classPrivateFieldGet(this, _EditorImpl_renderPreview, "f");
    }
    get currentEditor() {
        const type = this.currentType;
        switch (type) {
            case 'markup':
                return this.markupEditor;
            case 'wysiwyg':
                return this.wysiwygEditor;
            default:
                throw new Error('Unknown editor type: ' + type);
        }
    }
    get wysiwygEditor() {
        if (!__classPrivateFieldGet(this, _EditorImpl_wysiwygEditor, "f")) {
            __classPrivateFieldSet(this, _EditorImpl_wysiwygEditor, new WysiwygEditor({
                initialContent: __classPrivateFieldGet(this, _EditorImpl_markup, "f"),
                extensions: __classPrivateFieldGet(this, _EditorImpl_extensions, "f"),
                allowHTML: __classPrivateFieldGet(this, _EditorImpl_allowHTML, "f"),
                linkify: __classPrivateFieldGet(this, _EditorImpl_linkify, "f"),
                linkifyTlds: __classPrivateFieldGet(this, _EditorImpl_linkifyTlds, "f"),
                attrs: __classPrivateFieldGet(this, _EditorImpl_attrs, "f"),
                onChange: () => this.emit('rerender-toolbar', null),
                onDocChange: () => this.emit('change', null),
            }), "f");
        }
        return __classPrivateFieldGet(this, _EditorImpl_wysiwygEditor, "f");
    }
    get markupEditor() {
        if (!__classPrivateFieldGet(this, _EditorImpl_markupEditor, "f")) {
            const config = Object.assign(Object.assign({}, cmBaseConfig), { placeholder: i18n('markup_placeholder'), extraKeys: Object.assign(Object.assign({}, cmBaseConfig.extraKeys), { [f.toCM(A.Cancel)]: () => {
                        this.emit('cancel', null);
                    }, [f.toCM(A.Submit)]: () => {
                        this.emit('submit', null);
                    } }) });
            __classPrivateFieldSet(this, _EditorImpl_markupEditor, new MarkupEditor(CodeMirror(() => { }, Object.assign({ value: __classPrivateFieldGet(this, _EditorImpl_markup, "f") }, config))), "f");
            __classPrivateFieldGet(this, _EditorImpl_markupEditor, "f").codemirror.on('changes', this.onCMChanges);
            __classPrivateFieldGet(this, _EditorImpl_markupEditor, "f").codemirror.on('cursorActivity', this.onCMCursorActivity);
            __classPrivateFieldGet(this, _EditorImpl_markupEditor, "f").codemirror.on('paste', this.onCMPaste);
            __classPrivateFieldGet(this, _EditorImpl_markupEditor, "f").codemirror.on('beforeChange', this.onCMBeforeChange);
            if (__classPrivateFieldGet(this, _EditorImpl_fileUploadHandler, "f")) {
                __classPrivateFieldSet(this, _EditorImpl_cmFilesUploader, new CMFilesUploadManager({
                    cm: this.cm,
                    reactRenderer: __classPrivateFieldGet(this, _EditorImpl_renderStorage, "f"),
                    uploadHandler: __classPrivateFieldGet(this, _EditorImpl_fileUploadHandler, "f"),
                    needDimmensionsForImages: this.needToSetDimensionsForUploadedImages,
                }), "f");
            }
        }
        return __classPrivateFieldGet(this, _EditorImpl_markupEditor, "f");
    }
    get renderStorage() {
        return __classPrivateFieldGet(this, _EditorImpl_renderStorage, "f");
    }
    get fileUploadHandler() {
        return __classPrivateFieldGet(this, _EditorImpl_fileUploadHandler, "f");
    }
    get needToSetDimensionsForUploadedImages() {
        return __classPrivateFieldGet(this, _EditorImpl_needToSetDimensionsForUploadedImages, "f");
    }
    // ---> implements CodeEditor
    get cm() {
        return this.markupEditor.cm;
    }
    // <--- implements CodeEditor
    // ---> implements ActionStorage
    get actions() {
        return this.wysiwygEditor.actions;
    }
    action(actionName) {
        return this.wysiwygEditor.action(actionName);
    }
    // <--- implements ActionStorage
    destroy() {
        var _a, _b;
        if (__classPrivateFieldGet(this, _EditorImpl_markupEditor, "f")) {
            __classPrivateFieldGet(this, _EditorImpl_markupEditor, "f").codemirror.off('changes', this.onCMChanges);
            __classPrivateFieldGet(this, _EditorImpl_markupEditor, "f").codemirror.off('cursorActivity', this.onCMCursorActivity);
            __classPrivateFieldGet(this, _EditorImpl_markupEditor, "f").codemirror.off('paste', this.onCMPaste);
            __classPrivateFieldGet(this, _EditorImpl_markupEditor, "f").codemirror.off('beforeChange', this.onCMBeforeChange);
            __classPrivateFieldGet(this, _EditorImpl_markupEditor, "f").codemirror.getWrapperElement().remove();
        }
        (_a = __classPrivateFieldGet(this, _EditorImpl_wysiwygEditor, "f")) === null || _a === void 0 ? void 0 : _a.destroy();
        __classPrivateFieldSet(this, _EditorImpl_markupEditor, undefined, "f");
        __classPrivateFieldSet(this, _EditorImpl_wysiwygEditor, undefined, "f");
        (_b = __classPrivateFieldGet(this, _EditorImpl_cmFilesUploader, "f")) === null || _b === void 0 ? void 0 : _b.destroy();
        __classPrivateFieldSet(this, _EditorImpl_cmFilesUploader, undefined, "f");
    }
    setEditorType(type) {
        this.changeEditorType({ type, reason: 'manually' });
    }
    changeEditorType(opts) {
        if (__classPrivateFieldGet(this, _EditorImpl_editorType, "f") === opts.type)
            return;
        this.currentType = opts.type;
        this.emit('rerender', null);
        if (opts.reason !== 'error-boundary') {
            this.emit('change-editor-type', opts);
        }
    }
    changeToolbarVisibility(opts) {
        __classPrivateFieldSet(this, _EditorImpl_toolbarVisible, opts.visible, "f");
        this.emit('rerender', null);
        this.emit('change-toolbar-visibility', opts);
    }
    changeSplitModeEnabled(opts) {
        if (__classPrivateFieldGet(this, _EditorImpl_splitModeEnabled, "f") === opts.splitModeEnabled)
            return;
        __classPrivateFieldSet(this, _EditorImpl_splitModeEnabled, opts.splitModeEnabled, "f");
        this.emit('rerender', null);
        this.emit('change-split-mode-enabled', opts);
    }
    focus() {
        return this.currentEditor.focus();
    }
    hasFocus() {
        return this.currentEditor.hasFocus();
    }
    isEmpty() {
        return this.currentEditor.isEmpty();
    }
    clear() {
        return this.currentEditor.clear();
    }
    replace(newMarkup) {
        return this.currentEditor.replace(newMarkup);
    }
    prepend(markup) {
        return this.currentEditor.prepend(markup);
    }
    append(markup) {
        return this.currentEditor.append(markup);
    }
    moveCursor(position) {
        if (typeof position === 'object') {
            return this.moveCursorToLine(position.line);
        }
        return this.currentEditor.moveCursor(position);
    }
    moveCursorToLine(line) {
        const type = this.currentType;
        switch (type) {
            case 'markup': {
                this.cm.setCursor(line, 0, { scroll: true });
                break;
            }
            case 'wysiwyg': {
                const node = this.wysiwygEditor.dom.querySelector(`[data-line="${line}"]`);
                if (node) {
                    const position = this._wysiwygView.posAtDOM(node, 0);
                    const { tr } = this._wysiwygView.state;
                    this._wysiwygView.dispatch(tr.setSelection(TextSelection.create(tr.doc, position)).scrollIntoView());
                }
                break;
            }
            default:
                throw new Error('Unknown editor type: ' + type);
        }
    }
    shouldReplaceMarkupEditorValue(markupValue, wysiwygValue) {
        var _a;
        const serializedEditorMarkup = (_a = __classPrivateFieldGet(this, _EditorImpl_wysiwygEditor, "f")) === null || _a === void 0 ? void 0 : _a.serializer.serialize(__classPrivateFieldGet(this, _EditorImpl_wysiwygEditor, "f").parser.parse(markupValue));
        return (serializedEditorMarkup === null || serializedEditorMarkup === void 0 ? void 0 : serializedEditorMarkup.trim()) !== wysiwygValue.trim();
    }
}
_EditorImpl_markup = new WeakMap(), _EditorImpl_editorType = new WeakMap(), _EditorImpl_toolbarVisible = new WeakMap(), _EditorImpl_splitModeEnabled = new WeakMap(), _EditorImpl_splitMode = new WeakMap(), _EditorImpl_renderPreview = new WeakMap(), _EditorImpl_wysiwygEditor = new WeakMap(), _EditorImpl_markupEditor = new WeakMap(), _EditorImpl_allowHTML = new WeakMap(), _EditorImpl_linkify = new WeakMap(), _EditorImpl_linkifyTlds = new WeakMap(), _EditorImpl_attrs = new WeakMap(), _EditorImpl_extensions = new WeakMap(), _EditorImpl_renderStorage = new WeakMap(), _EditorImpl_fileUploadHandler = new WeakMap(), _EditorImpl_needToSetDimensionsForUploadedImages = new WeakMap(), _EditorImpl_prepareRawMarkup = new WeakMap(), _EditorImpl_cmFilesUploader = new WeakMap();
